import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { Button, Card, Col, message, Modal, Row, Spin, Typography, Input } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Box, Text } from 'atoms';
import { ACL } from 'components/common';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';
import {
	approveVendorBankDetails,
	rejectVendorBankDetails,
	getApprovalDetailsV2,
} from 'store/approvalSliceV2';

const { Title } = Typography;

const PageContainer = styled(Box)`
	padding: 24px;
	background: ${(props) => (props.$fromApproval ? '#fff' : '#f5f5f5')};
	min-height: calc(100vh - 64px);
`;

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const HeaderRight = styled.div`
	display: flex;
	gap: 12px;
`;

const RequestId = styled(Title)`
	margin: 0 !important;
	font-size: 20px;
	font-weight: 600;
`;

const CloseIconButton = styled(Button)`
	border: none;
	box-shadow: none;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;

	&:hover {
		background-color: #f5f5f5;
	}
`;

const ActionButtonContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 16px;
	margin-top: 32px;
	padding-top: 24px;
	border-top: 1px solid #e8e8e8;
`;

const RejectButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
	}
`;

const ApproveButtonStyled = styled(Button)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 8px 24px;
	height: 40px;
	background: #52c41a;
	color: #fff;
	border: 1.5px solid #52c41a;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	min-width: 120px;

	&:hover {
		background: #73d13d;
		border-color: #73d13d;
	}

	&:active {
		background: #389e0d;
		border-color: #389e0d;
	}
`;

const DetailsCard = styled(Card)`
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	margin-bottom: 24px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	display: block;
	margin-bottom: 4px;
`;

const FieldValue = styled(Text)`
	font-size: 14px;
	color: #000;
	font-weight: 500;
	display: block;

	/* Ensure children are valid React nodes */
	& > * {
		display: inline;
	}
`;

const DocumentLink = styled.a`
	color: #1890ff;
	text-decoration: none;
	font-size: 14px;
	font-weight: 500;

	&:hover {
		text-decoration: underline;
		color: #40a9ff;
	}
`;

const { TextArea } = Input;

const APPROVALS_VENDOR_BANK_DETAILS_PATH = '/approvals/vendor-bank-details';

const maskAccountNumber = (accountNumber) => {
	if (!accountNumber) return '-';
	if (accountNumber.length <= 8) return accountNumber;
	return `${accountNumber.substring(0, 3)}${'*'.repeat(
		accountNumber.length - 6
	)}${accountNumber.substring(accountNumber.length - 3)}`;
};

function VendorBankDetailsDetailsPageV2() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [details, setDetails] = useState(null);
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [comments, setComments] = useState('');
	const [rejectionReason, setRejectionReason] = useState('');
	const [actionLoading, setActionLoading] = useState(false);
	const [previewVisible, setPreviewVisible] = useState(false);

	// Check if accessed from approval page
	const isApprovalRoute = location.pathname.startsWith('/approvals/vendor-bank-details');
	const fromApproval = location.state?.fromApproval || isApprovalRoute;
	const approvalIdFromState = location.state?.approvalId || null;
	// If accessed from approval route, the id param is the approval ID
	const approvalId = isApprovalRoute ? id : approvalIdFromState;

	useEffect(() => {
		if (id && isApprovalRoute) {
			fetchApprovalDetails();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, isApprovalRoute]);

	// Helper function to format address object to string
	const formatAddress = (address) => {
		if (!address) return '-';
		if (typeof address === 'string') return address;
		if (typeof address !== 'object') return '-';

		// Format address object with address_line1, city, pincode, state
		const parts = [];
		if (address.address_line1) parts.push(address.address_line1);
		if (address.address_line2) parts.push(address.address_line2);
		if (address.city) parts.push(address.city);
		if (address.state) parts.push(address.state);
		if (address.pincode) parts.push(address.pincode);
		if (address.country) parts.push(address.country);

		// Also check for alternative field names
		if (address.line1 && !address.address_line1) parts.push(address.line1);
		if (address.line2 && !address.address_line2) parts.push(address.line2);

		return parts.length > 0 ? parts.join(', ') : '-';
	};

	// Helper functions to extract bank details from approval data
	const extractVendorName = (entitySummary, entityData, relatedData, entityDetails) =>
		entityDetails?.vendor_name ||
		entitySummary.vendor_name ||
		entityData.vendor_name ||
		relatedData.vendor?.name ||
		'-';

	const extractBankField = (entityData, field) =>
		entityData[field] || entityData.banking_info?.[field] || '-';

	const extractBankAddress = (entityData) => {
		const address =
			entityData.bank_address ||
			entityData.banking_info?.bank_address ||
			entityData.address ||
			null;
		return formatAddress(address);
	};

	const extractBranchName = (entityData) =>
		entityData.branch_name ||
		entityData.banking_info?.branch_name ||
		entityData.bank_branch ||
		'-';

	/**
	 * Extract document_key from entity_data.banking_info.document_info.branch_document.document_key
	 */
	const extractDocumentKey = (entityData) =>
		entityData.banking_info?.document_info?.branch_document?.document_key || null;

	/**
	 * Extract filename from document_key (remove UUID prefix)
	 */
	const extractFilenameFromS3Key = (s3Key) => {
		if (!s3Key) return '';
		const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;
		const match = s3Key.match(uuidPattern);
		if (match) {
			return s3Key.substring(match[0].length);
		}
		return s3Key;
	};

	const buildBankDetails = (approvalData) => {
		const entityData = approvalData.approval.entity_data || {};
		const entitySummary = approvalData.approval.entity_summary || {};
		const relatedData = approvalData.related_data || {};
		const entityDetails = approvalData.entity_details || {};

		const documentKey = extractDocumentKey(entityData);
		const documentName = documentKey ? extractFilenameFromS3Key(documentKey) : null;

		return {
			vendor_name: extractVendorName(entitySummary, entityData, relatedData, entityDetails),
			account_number: extractBankField(entityData, 'account_number'),
			ifsc_code: extractBankField(entityData, 'ifsc_code'),
			bank_name: extractBankField(entityData, 'bank_name'),
			branch_name: extractBranchName(entityData),
			account_holder_name: extractBankField(entityData, 'account_holder_name'),
			account_type: extractBankField(entityData, 'account_type'),
			bank_address: extractBankAddress(entityData),
			document_key: documentKey,
			document_name: documentName,
			created_by: approvalData.approval.requested_by_name || '-',
		};
	};

	const fetchApprovalDetails = async () => {
		setLoading(true);
		try {
			const approvalData = await dispatch(getApprovalDetailsV2({ approval_id: id }));

			if (!approvalData || !approvalData.approval) {
				message.error('Approval not found');
				navigate(APPROVALS_VENDOR_BANK_DETAILS_PATH);
				return;
			}

			const bankDetails = buildBankDetails(approvalData);
			setDetails(bankDetails);
		} catch (error) {
			console.error('Error fetching approval details:', error);
			message.error('Failed to fetch approval details');
			navigate(APPROVALS_VENDOR_BANK_DETAILS_PATH);
		} finally {
			setLoading(false);
		}
	};

	const handleApproveClick = () => {
		setApproveModalVisible(true);
		setComments('');
	};

	const handleRejectClick = () => {
		setRejectModalVisible(true);
		setRejectionReason('');
	};

	const handleApproveConfirm = async () => {
		if (!approvalId) {
			message.error('Approval ID not found');
			return;
		}

		if (!comments.trim()) {
			message.error('Comments are required for approval');
			return;
		}

		setActionLoading(true);
		try {
			const result = await dispatch(
				approveVendorBankDetails({
					approval_id: approvalId,
					comments,
				})
			);

			if (result.success) {
				setApproveModalVisible(false);
				setComments('');
				message.success('Bank details approved successfully');
				navigate(APPROVALS_VENDOR_BANK_DETAILS_PATH);
			}
		} catch (error) {
			console.error('Error approving bank details:', error);
			message.error('Failed to approve bank details');
		} finally {
			setActionLoading(false);
		}
	};

	const handleRejectConfirm = async () => {
		if (!approvalId) {
			message.error('Approval ID not found');
			return;
		}

		if (!rejectionReason.trim()) {
			message.error('Rejection reason is required');
			return;
		}

		setActionLoading(true);
		try {
			const result = await dispatch(
				rejectVendorBankDetails({
					approval_id: approvalId,
					rejection_reason: rejectionReason,
				})
			);

			if (result.success) {
				setRejectModalVisible(false);
				setRejectionReason('');
				message.success('Bank details rejected successfully');
				navigate(APPROVALS_VENDOR_BANK_DETAILS_PATH);
			}
		} catch (error) {
			console.error('Error rejecting bank details:', error);
			message.error('Failed to reject bank details');
		} finally {
			setActionLoading(false);
		}
	};

	if (loading) {
		return (
			<PageContainer $fromApproval={fromApproval}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Spin size="large" tip="Loading bank details..." />
				</Box>
			</PageContainer>
		);
	}

	if (!details) {
		return (
			<PageContainer $fromApproval={fromApproval}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
					<Text>Bank details not found</Text>
				</Box>
			</PageContainer>
		);
	}

	return (
		<PageContainer $fromApproval={fromApproval}>
			{fromApproval && (
				<HeaderContainer>
					<HeaderLeft>
						<RequestId>Request ID: {id}</RequestId>
					</HeaderLeft>
					<HeaderRight>
						<CloseIconButton
							icon={<CloseOutlined />}
							onClick={() => navigate(APPROVALS_VENDOR_BANK_DETAILS_PATH)}
						/>
					</HeaderRight>
				</HeaderContainer>
			)}

			<DetailsCard>
				<Row gutter={[24, 24]}>
					<Col xs={24} sm={8}>
						<FieldLabel>Vendor Name</FieldLabel>
						<FieldValue>{details.vendor_name || '-'}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Bank Account Number</FieldLabel>
						<FieldValue>{maskAccountNumber(details.account_number)}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>IFSC Code</FieldLabel>
						<FieldValue>{details.ifsc_code || '-'}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Bank Name</FieldLabel>
						<FieldValue>{details.bank_name || '-'}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Bank Branch</FieldLabel>
						<FieldValue>{details.branch_name || '-'}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Account Holder Name</FieldLabel>
						<FieldValue>{details.account_holder_name || '-'}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Account Type</FieldLabel>
						<FieldValue>{details.account_type || '-'}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Bank Address</FieldLabel>
						<FieldValue>{details.bank_address}</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Document Attached</FieldLabel>
						<FieldValue>
							{details.document_key ? (
								<DocumentLink
									onClick={() => setPreviewVisible(true)}
									style={{ cursor: 'pointer' }}
								>
									{details.document_name || 'View Document'}
								</DocumentLink>
							) : (
								'-'
							)}
						</FieldValue>
					</Col>
					<Col xs={24} sm={8}>
						<FieldLabel>Created By</FieldLabel>
						<FieldValue>{details.created_by || '-'}</FieldValue>
					</Col>
				</Row>
			</DetailsCard>

			{fromApproval && (
				<ActionButtonContainer>
					<ACL action="update" module="handle_vendor_bank_details_approval">
						<RejectButtonStyled onClick={handleRejectClick} disabled={actionLoading}>
							<CloseOutlined />
							Reject
						</RejectButtonStyled>
					</ACL>
					<ACL action="update" module="handle_vendor_bank_details_approval">
						<ApproveButtonStyled onClick={handleApproveClick} disabled={actionLoading}>
							<CheckOutlined />
							Approve
						</ApproveButtonStyled>
					</ACL>
				</ActionButtonContainer>
			)}

			{/* Approve Modal */}
			<Modal
				title="Approve Vendor Bank Details"
				open={approveModalVisible}
				onOk={handleApproveConfirm}
				onCancel={() => {
					setApproveModalVisible(false);
					setComments('');
				}}
				okText="Approve"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading || !comments.trim(),
				}}
			>
				<Box>
					<Text
						display="block"
						style={{
							fontSize: '14px',
							color: '#666',
							marginBottom: 24,
							lineHeight: 1.5,
						}}
					>
						This bank details will be approved and you won&apos;t be able to revert this
						change.
					</Text>
					<Box mb={3} textAlign="left">
						<Text
							fontSize="14px"
							color="#333"
							mb={1}
							display="block"
							style={{ textAlign: 'left' }}
						>
							Comments <span style={{ color: '#ff4d4f' }}>*</span>
						</Text>
						<TextArea
							rows={4}
							value={comments}
							onChange={(e) => setComments(e.target.value)}
							placeholder="Enter comments for approval"
							maxLength={500}
							showCount
						/>
					</Box>
				</Box>
			</Modal>

			{/* Reject Modal */}
			<Modal
				title="Reject Vendor Bank Details"
				open={rejectModalVisible}
				onOk={handleRejectConfirm}
				onCancel={() => {
					setRejectModalVisible(false);
					setRejectionReason('');
				}}
				okText="Reject"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading || !rejectionReason.trim(),
					danger: true,
				}}
			>
				<Box>
					<Text
						display="block"
						style={{
							fontSize: '14px',
							color: '#666',
							marginBottom: 24,
							lineHeight: 1.5,
						}}
					>
						This bank details will be rejected and you won&apos;t be able to revert this
						change.
					</Text>
					<Box mb={3} textAlign="left">
						<Text
							fontSize="14px"
							color="#333"
							mb={1}
							display="block"
							style={{ textAlign: 'left' }}
						>
							Rejection Reason <span style={{ color: '#ff4d4f' }}>*</span>
						</Text>
						<TextArea
							rows={4}
							value={rejectionReason}
							onChange={(e) => setRejectionReason(e.target.value)}
							placeholder="Enter rejection reason"
							maxLength={500}
							showCount
						/>
					</Box>
				</Box>
			</Modal>

			{/* Document Preview Modal */}
			{details?.document_key && (
				<FilePreviewModal
					visible={previewVisible}
					fileKey={details.document_key}
					fileName={details.document_name || 'Bank Document'}
					entity="vendor"
					onClose={() => setPreviewVisible(false)}
				/>
			)}
		</PageContainer>
	);
}

export default VendorBankDetailsDetailsPageV2;
